import { MarketLocation, useLocale } from '@e2m-customer-portal/hooks';
import { isBattery, isBiogas, isControlEnergy, isFluctuating } from '@e2m-customer-portal/shared';
import { Typography, useTheme } from '@mui/material';
import clsx from 'clsx';
import { setMonth } from 'date-fns';
import { range } from 'ramda';
import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AnnualOverviewRow } from './annual-overview-row/AnnualOverviewRow';

export interface FeedInAnnualOverviewPlaceholderProps {
  marketLocation: MarketLocation;
  isMobile?: boolean;
}

export function FeedInAnnualOverviewPlaceholder({
  marketLocation,
  isMobile,
}: Readonly<FeedInAnnualOverviewPlaceholderProps>): ReactElement<FeedInAnnualOverviewPlaceholderProps> {
  const { t } = useTranslation();
  const theme = useTheme();
  const { code: locale } = useLocale();

  const styles = {
    shared: 'py-2 bg-blue-grey-200 text-right text-white',
  };

  return (
    <div className="grid grid-cols-[2fr_repeat(13,1fr)] overflow-x-auto">
      <div
        className={clsx(
          styles.shared,
          'sticky left-0 flex flex-col items-center justify-end border-0 border-r border-solid border-grey-200 pl-6 pr-4 shadow-lg shadow-blue-grey-200 md:shadow-none xl:flex-row',
        )}
        data-testid="grid-header-cell"
      />

      {range(0, 12).map((month, index) => {
        const date = setMonth(new Date(), month);
        const isCurrentMonth = date.getMonth() === new Date().getMonth();

        return (
          <div
            key={month}
            className={clsx(
              styles.shared,
              'flex min-w-fit items-center justify-center border-0 border-l border-solid border-grey-200',
              {
                'border-l-0': !index,
              },
            )}
            data-testid="grid-header-cell"
          >
            <span
              className={clsx(isCurrentMonth ? 'px-2 my-auto pb-1 leading-none rounded-full bg-green-200' : 'mb-1')}
            >
              <Typography className="min-[1800px]:hidden" variant="body3">
                {date.toLocaleDateString(locale, { month: 'short' })}
              </Typography>
              <Typography className="hidden min-[1800px]:inline-block" variant="body3">
                {date.toLocaleDateString(locale, { month: 'long' })}
              </Typography>
            </span>
          </div>
        );
      })}

      <div
        className={clsx(
          styles.shared,
          'flex items-center justify-end border-0 border-l border-solid border-grey-200 pl-4 pr-6',
        )}
        data-testid="grid-header-cell"
      >
        <Typography variant="body3">{t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_YEAR')}</Typography>
      </div>

      {!isFluctuating(marketLocation.technologies) && (
        <AnnualOverviewRow
          rowTitle={
            isMobile
              ? t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_FORECASTED_MOBILE')
              : t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_FORECASTED')
          }
          unit="MWh"
          contextInfo={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_FORECASTED_TOOLTIP')}
          color={theme.palette.charts.colors[1]}
          placeholderMode="month"
        />
      )}

      {!isFluctuating(marketLocation.technologies) && !isControlEnergy(marketLocation.technologies) && (
        <AnnualOverviewRow
          rowTitle={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_FORECASTED_MEAN')}
          unit="kW"
          contextInfo={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_FORECASTED_MEAN_TOOLTIP')}
          placeholderMode="month"
        />
      )}

      <AnnualOverviewRow
        rowTitle={
          isMobile
            ? t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_PRODUCTION_MOBILE')
            : t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_PRODUCTION')
        }
        unit="MWh"
        contextInfo={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_PRODUCTION_TOOLTIP')}
        color={theme.palette.charts.colors[6]}
        placeholderMode="month"
      />

      {marketLocation.linkedMarketLocation && (
        <AnnualOverviewRow
          rowTitle={
            isMobile
              ? t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_CONSUMPTION_MOBILE')
              : t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_CONSUMPTION')
          }
          unit="MWh"
          color={theme.palette.charts.colors[6]}
          placeholderMode="month"
        />
      )}

      <AnnualOverviewRow
        rowTitle={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_PRODUCTION_MEAN')}
        unit="kW"
        contextInfo={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_PRODUCTION_MEAN_TOOLTIP')}
        placeholderMode="month"
      />

      {marketLocation.linkedMarketLocation && (
        <AnnualOverviewRow
          rowTitle={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_CONSUMPTION_MEAN')}
          unit="kW"
          placeholderMode="month"
        />
      )}

      {isBiogas(marketLocation.technologies) && (
        <AnnualOverviewRow
          rowTitle={
            <Trans components={{ sub: <sub /> }}>
              MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_MEASUREMENT_ABOVE_85_PERCENT_INSTALLED_POWER
            </Trans>
          }
          contextInfo={t(
            'MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_MEASUREMENT_ABOVE_85_PERCENT_INSTALLED_POWER_TOOLTIP',
          )}
          placeholderMode="month"
        />
      )}
      {marketLocation.permissions.seeRevenue &&
        !isFluctuating(marketLocation.technologies) &&
        !isBattery(marketLocation.technologies) && (
          <AnnualOverviewRow
            rowTitle={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_SPECIFIC_ADDITIONAL_REVENUE')}
            unit="ct/kWh"
            contextInfo={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_SPECIFIC_ADDITIONAL_REVENUE_TOOLTIP')}
            placeholderMode="month"
          />
        )}
    </div>
  );
}
